import { useEffect, useRef, useState } from 'react';
import type { AIModel, Message } from '@volt/shared';
import { chapterTitle } from '../lib/chapters.js';
import { Icon } from './Icon.js';
import { hapticFeedback } from '../lib/haptics.js';
import { useI18n } from '../i18n/context.js';
import { EMPTY_REASONING, reasoningTextFor, type ReasoningByMessageId } from '../lib/reasoningStream.js';
import { MessageBubble } from './MessageBubble.js';
import { Modal } from './Modal.js';
import { useSession } from '../state/session.js';

interface MessageListProps {
  messages: Message[];
  models: AIModel[];
  loading: boolean;
  streaming: boolean;
  thinking: boolean;
  /** Live processing explanations, keyed by assistant message id. */
  reasoningByMessageId?: ReasoningByMessageId;
  onShare?: (conversationId: string, messageId?: string) => void;
  onRegenerate?: (assistantMessageId: string, modelId: string) => void;
  onFeedback?: (messageId: string, feedback: 'up' | 'down' | null) => void;
  onEditMessage?: (userMessageId: string, text: string, modelId: string) => Promise<void>;
  onAttachContext?: (message: Message) => void;
  onRewind?: (messageId: string) => void;
  onSetChapter?: (messageId: string, chapter: boolean) => void;
  failedSendId?: string | null;
  onRetrySend?: () => void;
  /** Pick a starter prompt on the empty chat. */
  onSuggestion?: (text: string) => void;
  /** Pulled up past the end of the chat: start a new one. */
  onPullNewChat?: () => void;
  activeModelId: string;
  selectionMode?: boolean;
  selectedMessageIds?: Set<string>;
  onToggleSelection?: (id: string) => void;
  /** Sends the answers of a clarifying quiz as the next message. */
  onSubmitQuiz?: (text: string) => void;
}

const SUGGESTION_KEYS = [
  'suggest.explain', 'suggest.write', 'suggest.translate', 'suggest.summarize', 'suggest.ideas',
] as const;

/** Scrollable message thread. Auto-scrolls to the bottom during streaming. */
export function MessageList({
  messages,
  models,
  loading,
  streaming,
  thinking,
  reasoningByMessageId = EMPTY_REASONING,
  onShare,
  onRegenerate,
  onFeedback,
  onEditMessage,
  onAttachContext,
  onRewind,
  onSetChapter,
  failedSendId = null,
  onRetrySend,
  onSuggestion,
  onPullNewChat,
  activeModelId,
  selectionMode = false,
  selectedMessageIds,
  onToggleSelection,
  onSubmitQuiz,
}: MessageListProps) {
  const { t } = useI18n();
  const { data } = useSession();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [editText, setEditText] = useState('');
  const [editPending, setEditPending] = useState(false);
  const [editError, setEditError] = useState(false);
  const editVersionRef = useRef(0);
  const conversationId = messages[0]?.conversationId;
  function handleToggleChapter(id: string) {
    const message = messages.find((m) => m.id === id);
    if (message) onSetChapter?.(id, !message.isChapter);
  }
  function scrollToMessage(id: string) {
    const el = document.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(id)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  const chapters = messages.filter((m) => m.isChapter);
  useEffect(() => {
    editVersionRef.current += 1;
    setEditingMessage(null);
    setEditPending(false);
    setEditError(false);
    return () => { editVersionRef.current += 1; };
  }, [conversationId]);

  // Smart auto-scroll: follow new content only while the reader is at the bottom.
  const listRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const lastCountRef = useRef(0);
  const lastConversationRef = useRef<string | undefined>(undefined);
  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(0);

  const scrollToBottom = (smooth: boolean) => {
    stickRef.current = true;
    setAtBottom(true);
    setUnseen(0);
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
  };

  useEffect(() => {
    const switched = lastConversationRef.current !== conversationId;
    const grew = messages.length > lastCountRef.current;
    const last = messages[messages.length - 1];
    const ownSend = grew && last?.role === 'user' && last.userId === (data?.user.id ?? '') ||
      grew && last?.role === 'user' && last.id.startsWith('local-');
    lastConversationRef.current = conversationId;
    if (switched || ownSend || stickRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: streaming || switched ? 'instant' : 'smooth' });
      stickRef.current = true;
      setUnseen(0);
    } else if (grew) {
      setUnseen((n) => n + (messages.length - lastCountRef.current));
    }
    lastCountRef.current = messages.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, streaming]);

  const hasMessages = messages.length > 0;
  // When the keyboard opens/closes the list is resized: keep the latest
  // message in view if the reader was at the bottom.
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (stickRef.current) bottomRef.current?.scrollIntoView({ behavior: 'instant' });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, hasMessages]);

  // Pull up past the end of the chat to reveal "New chat" (like ChatGPT on iOS).
  const pullRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    const hint = pullRef.current;
    if (!el || !hint || !onPullNewChat) return;
    const THRESHOLD = 88;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let pulling = false;
    let armed = false;
    const atBottom = () => el.scrollHeight - el.scrollTop - el.clientHeight <= 1;
    const apply = (dy: number) => {
      const offset = Math.min(dy * 0.5, 110);
      el.style.transform = offset ? `translateY(${-offset}px)` : '';
      hint.style.setProperty('--pull', String(Math.min(1, dy / THRESHOLD)));
    };
    const reset = (animate: boolean) => {
      el.style.transition = animate ? 'transform 0.25s cubic-bezier(0.22, 0.8, 0.3, 1)' : '';
      apply(0);
      hint.classList.remove('is-armed');
      document.documentElement.removeAttribute('data-pulling');
      pulling = false;
      armed = false;
      tracking = false;
    };
    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0]!;
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = atBottom();
      el.style.transition = '';
    };
    const onMove = (event: TouchEvent) => {
      if (!tracking) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dy = startY - touch.clientY;
      const dx = Math.abs(touch.clientX - startX);
      if (!pulling) {
        if (dy < -4 || dx > Math.max(10, dy)) { tracking = false; return; }
        if (dy > 8 && atBottom()) {
          pulling = true;
          document.documentElement.setAttribute('data-pulling', '');
        } else return;
      }
      event.preventDefault();
      apply(dy);
      const nowArmed = dy >= THRESHOLD;
      if (nowArmed !== armed) {
        armed = nowArmed;
        hint.classList.toggle('is-armed', armed);
        if (armed) hapticFeedback('tap');
      }
    };
    const onEnd = () => {
      const fire = pulling && armed;
      reset(true);
      if (fire) {
        hapticFeedback('select');
        onPullNewChat();
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      reset(false);
    };
  }, [onPullNewChat, loading, hasMessages]);

  function handleScroll() {
    const el = listRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickRef.current = near;
    setAtBottom(near);
    if (near) setUnseen(0);
  }

  function openEdit(userMessageId: string) {
    const msg = messages.find((m) => m.id === userMessageId);
    if (!msg || msg.role !== 'user' || msg.userId !== data?.user.id || streaming || msg.id.startsWith('local-')) return;
    editVersionRef.current += 1;
    setEditingMessage(msg);
    setEditText(msg.content);
    setEditError(false);
  }

  async function submitEdit() {
    if (!editingMessage || !editText.trim() || !onEditMessage || editPending || streaming) return;
    const version = editVersionRef.current;
    setEditPending(true);
    setEditError(false);
    try {
      await onEditMessage(editingMessage.id, editText.trim(), activeModelId);
      if (version === editVersionRef.current) setEditingMessage(null);
    } catch {
      if (version === editVersionRef.current) setEditError(true);
    } finally {
      if (version === editVersionRef.current) setEditPending(false);
    }
  }

  if (loading) {
    return (
      <div className="message-list message-list-loading">
        <span className="spinner" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="message-list message-list-empty">
        <p className="empty-hint">{t('common.empty')}</p>
        {onSuggestion && (
          <div className="suggestions" role="list">
            {SUGGESTION_KEYS.map((key) => {
              const text = t(key);
              return (
                <button
                  key={key}
                  type="button"
                  role="listitem"
                  className="suggestion-chip"
                  onClick={() => onSuggestion(text)}
                >
                  {text.replace(/[:\s]+$/, '')}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const lastIndex = messages.length - 1;

  return (
    <>
      {chapters.length > 0 && (
        <nav className="chapters-bar" aria-label={t('chapters.title')}>
          <Icon name="pin" size={14} />
          {chapters.map((m) => (
            <button key={m.id} type="button" className="chapters-chip" onClick={() => scrollToMessage(m.id)}>
              {chapterTitle(m.content)}
            </button>
          ))}
        </nav>
      )}
      <div className="message-list-wrap">
      <div className="message-list" role="log" aria-live="polite" ref={listRef} onScroll={handleScroll}>
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            message={message}
            models={models}
            isStreaming={streaming && index === lastIndex}
            isThinking={thinking && index === lastIndex}
            reasoning={
              message.role === 'assistant' ? reasoningTextFor(reasoningByMessageId, message.id) : null
            }
            onShare={onShare}
            onRegenerate={onRegenerate}
            onEdit={onEditMessage && !streaming && message.userId === data?.user.id && !message.id.startsWith('local-') ? openEdit : undefined}
            onFeedback={onFeedback}
            onAttachContext={onAttachContext}
            onRewind={streaming ? undefined : onRewind}
            sendFailed={failedSendId === message.id}
            onRetrySend={onRetrySend}
            isChapter={message.isChapter}
            onToggleChapter={onSetChapter ? handleToggleChapter : undefined}
            selectionMode={selectionMode}
            selected={selectedMessageIds?.has(message.id)}
            selectionDisabled={(selectedMessageIds?.size ?? 0) >= 100 && !selectedMessageIds?.has(message.id)}
            quizAnswerable={
              !streaming && index === lastIndex && message.role === 'assistant' && message.status === 'complete'
            }
            onSubmitQuiz={onSubmitQuiz}
            onToggleSelection={message.status === 'complete' && !message.id.startsWith('local-') &&
              (message.role === 'user' || message.role === 'assistant') && message.content.trim()
              ? onToggleSelection : undefined}
          />
        ))}
        <div ref={bottomRef} aria-hidden="true" />
      </div>
      {onPullNewChat && (
        <div className="pull-new-chat" ref={pullRef} aria-hidden="true">
          <span className="pull-new-chat-circle"><Icon name="edit" size={24} /></span>
          <span className="pull-new-chat-label">{t('chat.pullNewChat')}</span>
        </div>
      )}
      {!atBottom && (
        <button
          type="button"
          className="scroll-down-btn"
          aria-label={t('chat.scrollDown')}
          onClick={() => scrollToBottom(true)}
        >
          <Icon name="chevronDown" size={20} />
          {unseen > 0 && <span className="scroll-down-badge">{unseen > 99 ? '99+' : unseen}</span>}
        </button>
      )}
      </div>

      {/* Edit message modal */}
      <Modal
        open={editingMessage !== null}
        onClose={() => {
          editVersionRef.current += 1;
          setEditingMessage(null);
          setEditPending(false);
        }}
        title={t('message.edit')}
        closeLabel={t('common.cancel')}
        footer={
          <>
          <button type="button" className="btn btn-ghost" disabled={editPending} onClick={() => setEditingMessage(null)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!editText.trim() || editPending || streaming}
            onClick={() => void submitEdit()}
          >
            {t('common.save')}
          </button>
          </>
        }
      >
        <textarea
          className="field edit-textarea"
          rows={6}
          value={editText}
          disabled={editPending}
          aria-label={t('message.edit')}
          onChange={(event) => setEditText(event.target.value)}
          autoFocus
        />
        {editError ? <p role="alert" className="bubble-error">{t('message.editError')}</p> : null}
      </Modal>
    </>
  );
}
